import { useEffect, useState } from "react";
import { Menu, X, CalendarCheck, Phone, Leaf, ShieldCheck } from "lucide-react";
import { NAV_LINKS } from "../data/site";
import { scrollToId } from "../lib/smooth";

export const Header = ({ onBookVisit, phone = "+919163841785" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  const fmtPhone = phone.replace(/(\+91)(\d{5})(\d{5})/, "$1 $2 $3");

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div
        data-testid="announcement-bar"
        className={`bg-[#EAF5D6] overflow-hidden transition-all duration-500 ${scrolled ? "max-h-0" : "max-h-10 border-b border-[#DCECC0]"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-center sm:justify-between gap-4">
          <p className="flex items-center gap-2 font-mono-spec text-[9px] sm:text-[10px] font-semibold tracking-[0.16em] uppercase text-[#4D7C0F] min-w-0">
            <Leaf size={11} className="text-[#65A30D] shrink-0" />
            <span className="truncate">2 &amp; 3 BHK Residences · Hatishala, off Newtown Action Area III</span>
          </p>
          <p className="hidden md:flex items-center gap-2 font-mono-spec text-[10px] font-semibold tracking-[0.16em] uppercase text-[#4D7C0F] shrink-0">
            <ShieldCheck size={11} className="text-[#65A30D]" />
            Authorised Channel Partner: Forest Hill Realtors
          </p>
        </div>
      </div>
      <header
        data-testid="site-header"
        className={`transition-all duration-500 bg-[#FFFFFF]/95 backdrop-blur-xl border-b border-[#DCECC0] ${
          scrolled ? "shadow-[0_12px_32px_-16px_rgba(77,124,15,0.28)]" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
          <a
            href="#overview"
            data-testid="header-logo"
            onClick={(e) => go(e, "#overview")}
            className="flex items-center gap-2.5 leading-none min-w-0"
          >
            <span className="w-9 h-9 rounded-full bg-[#EAF5D6] border border-[#DCECC0] flex items-center justify-center shrink-0">
              <Leaf size={16} className="text-[#65A30D]" strokeWidth={1.8} />
            </span>
            <span className="min-w-0">
              <span className="font-serif-display text-lg sm:text-2xl text-[#3F6212] tracking-tight whitespace-nowrap">
                Forest Hill <span className="italic text-[#84CC16]">Realtors</span>
              </span>
              <span className="block text-[8px] tracking-[0.22em] uppercase text-[#65A30D] mt-0.5 font-mono-spec">
                Authorised Channel Partner
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => go(e, l.href)}
                className="relative text-[13px] font-sub font-semibold text-[#3F6212] hover:text-[#65A30D] tracking-wide transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-[#84CC16] after:rounded-full after:transition-all after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="header-call-link"
              href={`tel:${phone}`}
              className="hidden xl:flex items-center gap-2.5 pr-5 mr-1 border-r border-[#DCECC0] group"
            >
              <Phone size={16} className="text-[#65A30D] shrink-0" />
              <span className="leading-none">
                <span className="block text-[8px] font-mono-spec tracking-[0.2em] uppercase text-[#65A30D]">Call Us</span>
                <span className="block text-[13px] font-sub font-bold text-[#3F6212] group-hover:text-[#84CC16] transition-colors duration-300 mt-1 whitespace-nowrap">
                  {fmtPhone}
                </span>
              </span>
            </a>
            <button
              data-testid="header-book-site-visit-btn"
              onClick={onBookVisit}
              className="hidden sm:inline-flex items-center gap-2 bg-[#C2E87F] text-[#365314] text-[13px] font-sub font-bold px-5 py-2.5 rounded-full hover:bg-[#A9DB5E] hover:shadow-[0_10px_24px_-10px_rgba(132,204,22,0.6)] active:scale-[0.97] transition-all duration-300"
            >
              <CalendarCheck size={15} /> Book Site Visit
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-[#3F6212]"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-[#FFFFFF] border-t border-[#DCECC0] px-6 py-5 space-y-4 shadow-[0_24px_40px_-24px_rgba(77,124,15,0.25)]" data-testid="mobile-nav">
            <a
              data-testid="mobile-nav-call-link"
              href={`tel:${phone}`}
              className="flex items-center gap-3 pb-4 border-b border-[#E3EFCB]"
            >
              <span className="w-10 h-10 rounded-full bg-[#EAF5D6] border border-[#C2E87F] flex items-center justify-center shrink-0">
                <Phone size={16} className="text-[#65A30D]" />
              </span>
              <span className="leading-none">
                <span className="block text-[9px] font-mono-spec tracking-[0.2em] uppercase text-[#65A30D]">Call Us</span>
                <span className="block text-base font-sub font-bold text-[#3F6212] mt-1">
                  {fmtPhone}
                </span>
              </span>
            </a>
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => go(e, l.href)}
                className="block text-sm font-sub font-semibold text-[#3F6212]"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-nav-book-visit-btn"
              onClick={() => { setOpen(false); onBookVisit(); }}
              className="w-full bg-[#C2E87F] text-[#365314] text-sm font-sub font-bold px-5 py-3 rounded-full"
            >
              Book Site Visit
            </button>
          </div>
        )}
      </header>
    </div>
  );
};
